import apiClient from './client';

// ─── Typen ──────────────────────────────────────────────────────────────────

export interface ParsedPerson {
  first_name: string;
  last_name?: string | null;
  gender?: string | null;
  birth_date_text?: string | null;
  birth_place?: string | null;
  death_date_text?: string | null;
  death_place?: string | null;
  occupation?: string | null;
  notes?: string | null;
  is_living?: boolean;
  gedcom_xref?: string | null;
}

export interface ParsedRelationship {
  person1_ref: string;
  person2_ref: string;
  type: string;
  start_date_text?: string | null;
  start_place?: string | null;
  family_xref?: string | null;
}

export interface ParsedFamilyMember {
  child_ref: string;
  parent1_ref: string | null;
  parent2_ref: string | null;
  family_xref?: string | null;
}

export interface ParseResult {
  persons: ParsedPerson[];
  relationships: ParsedRelationship[];
  familyMembers: ParsedFamilyMember[];
  errors: string[];
}

export interface ImportReport {
  /** Neu angelegt. */
  imported: number;
  /** Bestehende Personen, die überschrieben wurden. */
  updated: number;
  /** Bestehende Personen, die unverändert blieben. */
  skipped: number;
  errors: string[];
}

export type ConflictStrategy = 'skip' | 'overwrite';

/** Dateityp der Import-Datei */
export type ImportFileType = 'gedcom' | 'csv';

/**
 * Erkennt den Dateityp anhand der Dateiendung.
 */
export function detectFileType(filename: string): ImportFileType | null {
  const ext = filename.split('.').pop()?.toLowerCase();
  if (ext === 'ged' || ext === 'gedcom') return 'gedcom';
  if (ext === 'csv') return 'csv';
  return null;
}

// ─── API-Funktionen ─────────────────────────────────────────────────────────

/**
 * Lädt eine Datei hoch und gibt eine Vorschau zurück (ohne DB-Änderungen).
 * Erkennt automatisch ob GEDCOM oder CSV.
 */
export async function previewImport(file: File, fileType: ImportFileType): Promise<ParseResult> {
  const formData = new FormData();
  formData.append('file', file);

  const endpoint = fileType === 'csv' ? '/import/csv' : '/import/gedcom';

  const { data } = await apiClient.post<ParseResult>(
    `${endpoint}?preview=true`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60_000,
    },
  );

  return data;
}

/**
 * Importiert eine Datei in die Datenbank.
 */
export async function executeImport(
  file: File,
  fileType: ImportFileType,
  strategy: ConflictStrategy,
): Promise<ImportReport> {
  const formData = new FormData();
  formData.append('file', file);

  const endpoint = fileType === 'csv' ? '/import/csv' : '/import/gedcom';

  const { data } = await apiClient.post<ImportReport>(
    `${endpoint}?preview=false&strategy=${strategy}`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120_000,
    },
  );

  return data;
}
